import { withJsonFormsControlProps } from "@jsonforms/react";
import { Dropdown, TextInput } from "pepsico-ds";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";

const SECONDS_IN_DAY = 86400;
const SECONDS_IN_HOUR = 3600;

const UNIT_OPTIONS = [
  { id: "days", displayText: "Days" },
  { id: "hours", displayText: "Hours" },
];

/**
 * Converts a seconds value back to { unit, amount } for display.
 * Prefers "days" if the value is divisible by 86400, otherwise "hours".
 */
const secondsToUnitAmount = (seconds) => {
  if (!seconds || seconds <= 0) return { unit: "days", amount: "" };
  if (seconds % SECONDS_IN_DAY === 0) {
    return { unit: "days", amount: String(seconds / SECONDS_IN_DAY) };
  }
  return { unit: "hours", amount: String(seconds / SECONDS_IN_HOUR) };
};

/**
 * Converts { unit, amount } to seconds.
 */
const unitAmountToSeconds = (unit, amount) => {
  const num = parseFloat(amount);
  if (!amount || isNaN(num) || num <= 0) return undefined;
  return unit === "days" ? num * SECONDS_IN_DAY : num * SECONDS_IN_HOUR;
};

const NotificationFrequencyComponent = (props) => {
  const { handleChange, data, path, enabled, visible } = props;

  // Initialise directly from data so edit mode renders the correct values on the first paint
  const [unit, setUnit] = useState(() => secondsToUnitAmount(data).unit);
  const [amount, setAmount] = useState(() => secondsToUnitAmount(data).amount);

  // Re-sync when the data prop changes from outside (e.g. form reset or external update)
  useEffect(() => {
    const { unit: parsedUnit, amount: parsedAmount } = secondsToUnitAmount(data);
    setUnit(parsedUnit);
    setAmount(parsedAmount);
  }, [data]);

  const commitChange = useCallback(
    (nextUnit, nextAmount) => {
      const seconds = unitAmountToSeconds(nextUnit, nextAmount);
      handleChange(path, seconds);
    },
    [handleChange, path]
  );

  const handleUnitChange = useCallback(
    (selected) => {
      const nextUnit = selected?.[0]?.id ?? "days";
      setUnit(nextUnit);
      commitChange(nextUnit, amount);
    },
    [amount, commitChange]
  );

  const handleAmountChange = useCallback(
    (nextAmount) => {
      setAmount(nextAmount);
      commitChange(unit, nextAmount);
    },
    [unit, commitChange]
  );

  const selectedUnit = UNIT_OPTIONS.filter((opt) => opt.id === unit);

  if (!visible) return null;

  return (
    <div
      style={{ display: "flex", alignItems: "flex-end", gap: "12px" }}
      data-testid="notification-frequency-control"
    >
      <div style={{ flex: 1, minWidth: 120 }}>
        <TextInput
          label={"Threshold"}
          type="number"
          value={amount}
          onUpdate={handleAmountChange}
          disabled={!enabled}
          min={1}
          step={1}
          placeholderText=""
        />
      </div>
      <div>
        <Dropdown
          childList={UNIT_OPTIONS}
          selection="single"
          label="Unit"
          selectedValue={selectedUnit}
          setSelectedValue={handleUnitChange}
          disabled={!enabled}
        />
      </div>
    </div>
  );
};

export const NotificationFrequencyControl = withJsonFormsControlProps(
  NotificationFrequencyComponent
);

NotificationFrequencyComponent.propTypes = {
  handleChange: PropTypes.func.isRequired,
  data: PropTypes.number,
  path: PropTypes.string.isRequired,
  enabled: PropTypes.bool,
  visible: PropTypes.bool,
};

NotificationFrequencyComponent.defaultProps = {
  enabled: true,
  visible: true,
};

export default NotificationFrequencyComponent;
